import baseTheme from '../theme';
import '../assets/fonts/salonFonts.css';


export const DEFAULT_SALON_PUBLIC_THEME = Object.freeze({
  version: 1,
  primary_color: '#CC9363',
  secondary_color: '#C79857',
  background_color: '#F8F4EE',
  font_key: 'saloone',
});

const latinSansStacks = (family) => ({
  ltr: Object.freeze([family, 'Cairo Variable', 'Segoe UI', 'Arial', 'sans-serif']),
  rtl: Object.freeze([family, 'Cairo Variable', 'Tahoma', 'Arial', 'sans-serif']),
});

const latinSerifStacks = (family) => ({
  ltr: Object.freeze([family, 'Cairo Variable', 'Georgia', 'Times New Roman', 'serif']),
  rtl: Object.freeze([family, 'Cairo Variable', 'Tahoma', 'Arial', 'serif']),
});

export const SALON_FONT_CATALOG = Object.freeze({
  saloone: Object.freeze({ label: 'Inter', ...latinSansStacks('Inter Variable') }),
  modern: Object.freeze({ label: 'Roboto', ...latinSansStacks('Roboto Variable') }),
  soft: Object.freeze({ label: 'Nunito', ...latinSansStacks('Nunito Variable') }),
  classic: Object.freeze({ label: 'Merriweather', ...latinSerifStacks('Merriweather Variable') }),
  open_sans: Object.freeze({ label: 'Open Sans', ...latinSansStacks('Open Sans Variable') }),
  lato: Object.freeze({ label: 'Lato', ...latinSansStacks('Lato') }),
  poppins: Object.freeze({ label: 'Poppins', ...latinSansStacks('Poppins') }),
  montserrat: Object.freeze({ label: 'Montserrat', ...latinSansStacks('Montserrat Variable') }),
  raleway: Object.freeze({ label: 'Raleway', ...latinSansStacks('Raleway Variable') }),
  playfair_display: Object.freeze({
    label: 'Playfair Display',
    ...latinSerifStacks('Playfair Display Variable'),
  }),
  cairo: Object.freeze({
    label: 'Cairo',
    ltr: Object.freeze(['Cairo Variable', 'Segoe UI', 'Arial', 'sans-serif']),
    rtl: Object.freeze(['Cairo Variable', 'Tahoma', 'Arial', 'sans-serif']),
  }),
  tajawal: Object.freeze({
    label: 'Tajawal',
    ltr: Object.freeze(['Tajawal', 'Segoe UI', 'Arial', 'sans-serif']),
    rtl: Object.freeze(['Tajawal', 'Tahoma', 'Arial', 'sans-serif']),
  }),
});

export const SALON_FONT_KEYS = Object.freeze(Object.keys(SALON_FONT_CATALOG));

export const SALON_FONT_PRESETS = Object.freeze(Object.fromEntries(
  SALON_FONT_KEYS.map((key) => [key, Object.freeze({
    ltr: SALON_FONT_CATALOG[key].ltr,
    rtl: SALON_FONT_CATALOG[key].rtl,
  })]),
));

const MUI_TYPOGRAPHY_VARIANTS = Object.freeze([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'subtitle1',
  'subtitle2',
  'body1',
  'body2',
  'caption',
  'overline',
  'button',
]);

const ALLOWED_KEYS = Object.freeze(Object.keys(DEFAULT_SALON_PUBLIC_THEME));
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

const deepCopy = (value) => JSON.parse(JSON.stringify(value));

const hexToRgb = (color) => {
  const value = color.slice(1);
  return [0, 2, 4].map((index) => parseInt(value.slice(index, index + 2), 16));
};

const roundHalfEven = (value) => {
  const lower = Math.floor(value);
  const fraction = value - lower;
  if (Math.abs(fraction - 0.5) < Number.EPSILON * 8) {
    return lower % 2 === 0 ? lower : lower + 1;
  }
  return Math.round(value);
};

const rgbToHex = (channels) => `#${channels
  .map((channel) => Math.max(0, Math.min(255, roundHalfEven(channel))))
  .map((channel) => channel.toString(16).padStart(2, '0').toUpperCase())
  .join('')}`;

const mix = (color, target, amount) => {
  const sourceChannels = hexToRgb(color);
  const targetChannels = hexToRgb(target);
  return rgbToHex(sourceChannels.map(
    (source, index) => source + ((targetChannels[index] - source) * amount),
  ));
};

const channelLuminance = (channel) => {
  const normalized = channel / 255;
  if (normalized <= 0.04045) {
    return normalized / 12.92;
  }
  return ((normalized + 0.055) / 1.055) ** 2.4;
};

const relativeLuminance = (color) => {
  const [red, green, blue] = hexToRgb(color);
  return (
    (0.2126 * channelLuminance(red))
    + (0.7152 * channelLuminance(green))
    + (0.0722 * channelLuminance(blue))
  );
};

export const contrastRatio = (first, second) => {
  const [lighter, darker] = [
    relativeLuminance(first),
    relativeLuminance(second),
  ].sort((left, right) => right - left);
  return (lighter + 0.05) / (darker + 0.05);
};

const contrastText = (background) => (
  contrastRatio('#000000', background) >= contrastRatio('#FFFFFF', background)
    ? '#000000'
    : '#FFFFFF'
);

const accessibleTone = (color, background, minimumRatio = 3) => {
  if (contrastRatio(color, background) >= minimumRatio) {
    return color;
  }
  const target = (
    contrastRatio('#000000', background) >= contrastRatio('#FFFFFF', background)
      ? '#000000'
      : '#FFFFFF'
  );
  for (let percentage = 1; percentage <= 100; percentage += 1) {
    const candidate = mix(color, target, percentage / 100);
    if (contrastRatio(candidate, background) >= minimumRatio) {
      return candidate;
    }
  }
  return target;
};

const colorScale = (base) => ({
  50: mix(base, '#FFFFFF', 0.92),
  100: mix(base, '#FFFFFF', 0.85),
  200: mix(base, '#FFFFFF', 0.70),
  300: mix(base, '#FFFFFF', 0.50),
  400: mix(base, '#FFFFFF', 0.25),
  500: mix(base, '#FFFFFF', 0.10),
  600: base,
  700: mix(base, '#000000', 0.15),
  800: mix(base, '#000000', 0.30),
  900: mix(base, '#000000', 0.45),
  950: mix(base, '#000000', 0.60),
});

const isDefaultConfig = (config) => ALLOWED_KEYS.every(
  (key) => config[key] === DEFAULT_SALON_PUBLIC_THEME[key],
);

export const validateSalonPublicThemeDraft = (config) => {
  if (config === null || Array.isArray(config) || typeof config !== 'object') {
    return {
      isValid: false,
      value: null,
      errors: { non_field_errors: ['Theme configuration must be an object.'] },
    };
  }

  const errors = {};
  const unknownFields = Object.keys(config)
    .filter((key) => !ALLOWED_KEYS.includes(key))
    .sort();
  if (unknownFields.length) {
    errors.unknown_fields = unknownFields;
  }

  const value = { ...DEFAULT_SALON_PUBLIC_THEME, ...config };
  if (value.version !== 1 || typeof value.version === 'boolean') {
    errors.version = ['Only theme version 1 is supported.'];
  }

  ['primary_color', 'secondary_color', 'background_color'].forEach((field) => {
    if (typeof value[field] !== 'string' || !HEX_COLOR.test(value[field])) {
      errors[field] = ['Enter a six-digit hexadecimal color.'];
    } else {
      value[field] = value[field].toUpperCase();
    }
  });

  if (!Object.prototype.hasOwnProperty.call(SALON_FONT_PRESETS, value.font_key)) {
    errors.font_key = ['Select a supported font preset.'];
  }

  if (Object.keys(errors).length) {
    return { isValid: false, value: null, errors };
  }
  return { isValid: true, value, errors: {} };
};

const derivePalette = (config) => {
  const compatibilityMode = isDefaultConfig(config);
  return {
    primary: colorScale(config.primary_color),
    secondary: colorScale(config.secondary_color),
    onPrimary: contrastText(config.primary_color),
    onSecondary: contrastText(config.secondary_color),
    onBackground: contrastText(config.background_color),
    focus: compatibilityMode
      ? config.primary_color
      : accessibleTone(config.primary_color, config.background_color),
    border: compatibilityMode
      ? config.secondary_color
      : accessibleTone(config.secondary_color, config.background_color),
    compatibilityMode,
  };
};

export const buildSalonPublicTheme = (config, direction = 'ltr') => {
  const validation = validateSalonPublicThemeDraft(config ?? {});
  const resolvedConfig = validation.isValid
    ? validation.value
    : { ...DEFAULT_SALON_PUBLIC_THEME };
  const palette = derivePalette(resolvedConfig);
  const normalizedDirection = direction === 'rtl' ? 'rtl' : 'ltr';
  const fontStack = [
    ...SALON_FONT_PRESETS[resolvedConfig.font_key][normalizedDirection],
  ];
  const tokens = deepCopy(baseTheme);

  if (!palette.compatibilityMode) {
    tokens.colors.primary = palette.primary;
    tokens.colors.secondary = palette.secondary;
    tokens.colors.background.primary = resolvedConfig.background_color;
    tokens.colors.text.primary = palette.onBackground;
    tokens.colors.text.inverse = palette.onPrimary;
    tokens.colors.border.light = palette.border;
    tokens.colors.border.medium = palette.border;
  }
  tokens.typography.fontFamily.sans = fontStack;
  tokens.salon = {
    primary: resolvedConfig.primary_color,
    secondary: resolvedConfig.secondary_color,
    background: resolvedConfig.background_color,
    surface: mix(resolvedConfig.background_color, '#FFFFFF', 0.55),
    surfaceMuted: mix(resolvedConfig.background_color, '#FFFFFF', 0.25),
    onPrimary: palette.onPrimary,
    onSecondary: palette.onSecondary,
    onBackground: palette.onBackground,
    focus: palette.focus,
    border: palette.border,
    compatibilityMode: palette.compatibilityMode,
  };

  const fontFamily = fontStack.join(', ');
  const typographyVariants = Object.fromEntries(
    MUI_TYPOGRAPHY_VARIANTS.map((variant) => [variant, { fontFamily }]),
  );
  const muiOverrides = {
    direction: normalizedDirection,
    palette: {
      primary: {
        main: resolvedConfig.primary_color,
        contrastText: palette.onPrimary,
      },
      secondary: {
        main: resolvedConfig.secondary_color,
        contrastText: palette.onSecondary,
      },
      background: {
        default: resolvedConfig.background_color,
        paper: tokens.salon.surface,
      },
      text: {
        primary: palette.onBackground,
      },
      divider: palette.border,
    },
    typography: {
      fontFamily,
      ...typographyVariants,
    },
    components: {
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              outline: `3px solid ${palette.focus}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: palette.border,
          },
        },
      },
    },
  };

  return {
    config: { ...resolvedConfig },
    tokens,
    muiOverrides,
  };
};
